import { Col, Divider, Flex, Row, Spin, Typography } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as CancelBlue } from '../../../assets/icons/cancel-blue.svg';
import PastTripsActionCard from './PastTripsActionCard';
import { useContext, useEffect, useRef, useState } from 'react';
import { HYPHEN, TRIP_VIDEO_STATUS } from '../../../utilities/constants';
import { API } from '../../../services';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import TimelineLayout from './TimelineLayout';
import FragmentVideo from './FragmentVideo';
import ActionConfirmationModal from './ActionConfirmationModal';
import EmptyCard from '../../../components/EmptyCard/EmptyCard';
import useApiPolling from '../../../hooks/useApiPolling';
import PropTypes from 'prop-types';
import { getErrorMessage } from '../../../utilities/commonFunctions';

const statusMap = {
  vod_request_processing_in_queue: TRIP_VIDEO_STATUS.INPROGRESS,
  vod_request_sent_to_device: TRIP_VIDEO_STATUS.INPROGRESS,
  vod_request_sent_to_device_retry: TRIP_VIDEO_STATUS.INPROGRESS,
  vod_request_ack_from_device: TRIP_VIDEO_STATUS.INPROGRESS,
  vod_uploaded_from_device: TRIP_VIDEO_STATUS.INPROGRESS,
  vod_thumbnail_request_sent_to_device: TRIP_VIDEO_STATUS.UNAVAILABLE,
  vod_request_rejected_max_limit: TRIP_VIDEO_STATUS.ERROR,
  vod_request_rejected_trip_ongoing: TRIP_VIDEO_STATUS.ERROR,
  vod_failed_from_device: TRIP_VIDEO_STATUS.ERROR,
  vod_emmc_failed_from_device: TRIP_VIDEO_STATUS.ERROR,
  vod_processed: TRIP_VIDEO_STATUS.AVAILABLE,
};

const { Text } = Typography;

const PastTripsContent = ({ selectedTrip, setSelectedTrip }) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [videoFragmentDetails, setVideoFragmentDetails] = useState(null);
  const [selectedFragmentDetails, setSelectedFragmentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const isInitialLoadRef = useRef(true);
  const [isActionConfirmationModalOpen, setIsActionConfirmationModalOpen] =
    useState(false);

  const getComputedFragment = vodStatus =>
    statusMap[vodStatus] || TRIP_VIDEO_STATUS.UNAVAILABLE;

  const fetchTripVideoFragmentDetails = async () => {
    const shouldShowLoading = isInitialLoadRef.current;
    try {
      if (shouldShowLoading) {
        setLoading(true);
      }
      const { data } = await API.getTripVideoFragmentDetails(
        selectedTrip?.trip_id
      );
      if (data) {
        const formattedData = data?.map((fragment, index) => ({
          key: fragment?.fragment_uuid || fragment?.fragment_sequence || index,
          device_id: fragment?.device_id ?? HYPHEN,
          trip_id: fragment?.trip_id ?? HYPHEN,
          trip_start_time: fragment?.trip_start_time ?? HYPHEN,
          trip_end_time: fragment?.trip_end_time ?? HYPHEN,
          // fragment_filename_device:
          //   fragment?.fragment_filename_device ?? HYPHEN,
          fragment_sequence: fragment?.fragment_sequence ?? HYPHEN,
          fragment_timestamp: fragment?.fragment_timestamp ?? HYPHEN,
          fragment_uuid: fragment?.fragment_uuid ?? HYPHEN,
          thumbnail_filename: fragment?.thumbnail_filename ?? HYPHEN,
          thumbnail: fragment?.thumbnail ?? HYPHEN,
          vod_status: getComputedFragment(fragment?.vod_status) ?? HYPHEN,
          // vod_thumbnail_request_sent_to_device_timestamp:
          //   fragment?.vod_thumbnail_request_sent_to_device_timestamp ?? HYPHEN,
          comment: fragment?.comment ?? '',
          is_bookmarked: fragment?.is_bookmarked ?? false,
          is_sos_altered: fragment?.is_sos_altered ?? false,
          // anomaly_message_ids: fragment?.anomaly_message_ids ?? [],
          // s3_objectkey: fragment?.s3_objectkey ?? HYPHEN,
          video_s3_link: fragment?.video_s3_link ?? HYPHEN,
        }));
        setVideoFragmentDetails(formattedData);
      }
    } catch (error) {
      if (shouldShowLoading) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch trip video fragment details! Please try again after sometime.'
          )
        );
      }
    } finally {
      if (shouldShowLoading) {
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    }
  };

  useEffect(() => {
    setVideoFragmentDetails(null);
    setSelectedFragmentDetails(null);
    isInitialLoadRef.current = true;
    setLoading(false);
  }, [selectedTrip]);

  useApiPolling(fetchTripVideoFragmentDetails, selectedTrip?.trip_id, [
    selectedTrip?.trip_id,
  ]);

  const requestTripVideoFragment = async () => {
    try {
      const payload = {
        trip_id: selectedTrip?.trip_id,
        fragment_uuid: selectedFragmentDetails?.fragment_uuid,
      };
      const { status, data } = await API.requestTripVideoFragment(payload);
      if (status === 200) {
        addErrorMessage(
          data?.message ||
            'Request sent to Cloud, please wait until it is processed.',
          'success'
        );
        // update the fragment status to INPROGRESS
        setVideoFragmentDetails(prevState =>
          prevState?.map(fragment =>
            fragment?.fragment_uuid === selectedFragmentDetails?.fragment_uuid
              ? {
                  ...fragment,
                  vod_status: TRIP_VIDEO_STATUS.INPROGRESS,
                }
              : fragment
          )
        );
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to request video fragment! Please try again after sometime.'
        )
      );
    } finally {
      setIsActionConfirmationModalOpen(false);
    }
  };

  const handleFragmentClick = fragment => {
    // if video exists - populate the video url and other details
    setSelectedFragmentDetails(fragment);

    // if video not available - open modal to request video from device
    if (fragment?.vod_status === TRIP_VIDEO_STATUS.UNAVAILABLE) {
      setIsActionConfirmationModalOpen(true);
    }
  };

  return (
    <Flex
      vertical
      className="full-width"
      style={{ backgroundColor: 'var(--primary-fill-color, #1a1a1d)' }}
    >
      {(!videoFragmentDetails || videoFragmentDetails?.length === 0) &&
        !loading && (
          <EmptyCard description="Selected trip does not have any videos." />
        )}
      {loading && (
        <Flex
          className="full-width full-height"
          align="center"
          justify="center"
        >
          <Spin size="large" />
        </Flex>
      )}
      {videoFragmentDetails?.length > 0 && !loading && (
        <>
          <Flex
            justify="space-between"
            align="center"
            style={{ padding: '19px 16px' }}
          >
            <Flex gap={16} align="center" justify="center">
              <Text
                strong
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                }}
              >
                {selectedTrip?.trip_id}
              </Text>
              {/* <Icon
                component={theme === 'dark' ? Copy : CopyBlack}
                onClick={() =>
                  navigator.clipboard.writeText(selectedTrip.trip_id)
                }
              /> */}
            </Flex>
            <Flex
              style={{ cursor: 'pointer' }}
              gap={8}
              onClick={() => setSelectedTrip(null)}
            >
              <Icon component={CancelBlue} />
              <Text style={{ color: '#0096E8' }}>Close trip</Text>
            </Flex>
          </Flex>
          <Divider />
          <Row gutter={10} className="content-body">
            <Col xs={{ span: 24 }} xl={{ span: 17 }} className="full-height">
              <Flex
                vertical
                gap={20}
                className="full-width full-height"
                style={{
                  padding: '24px 0px 24px 20px',
                }}
              >
                <TimelineLayout
                  fragments={videoFragmentDetails}
                  onFragmentClick={handleFragmentClick}
                  selectedFragmentDetails={selectedFragmentDetails}
                />
                <FragmentVideo
                  selectedTrip={selectedTrip}
                  fragmentDetails={selectedFragmentDetails}
                />
              </Flex>
            </Col>
            <Col xs={{ span: 24 }} xl={{ span: 7 }} className="full-height">
              <PastTripsActionCard
                selectedTrip={selectedTrip}
                selectedFragment={selectedFragmentDetails}
                setSelectedFragmentDetails={setSelectedFragmentDetails}
              />
            </Col>
          </Row>
          <ActionConfirmationModal
            isModalOpen={isActionConfirmationModalOpen}
            setIsModalOpen={setIsActionConfirmationModalOpen}
            actionType="fetchClip"
            selectedData={selectedFragmentDetails}
            handleAction={requestTripVideoFragment}
          />
        </>
      )}
    </Flex>
  );
};

PastTripsContent.propTypes = {
  selectedTrip: PropTypes.object,
  setSelectedTrip: PropTypes.func.isRequired,
};

export default PastTripsContent;
